/**
 * Getting a clip onto S3 and getting its path back, from the What's New page.
 *
 * Entries are authored as markdown on a branch, so the `video:` value has to be
 * known before the entry is written. The upload control itself already exists —
 * but it only appears inside the Notifications create/edit form, so the only way
 * to obtain a path was to open that form, upload, copy the path and abandon a
 * half-filled announcement. This is the same control with somewhere to stand.
 *
 * It renders the finished frontmatter LINE rather than the bare path, because
 * that is what actually gets pasted, and a path copied without its key is the
 * one mistake this panel exists to prevent.
 *
 * Collapsed by default and hidden on broker-admin builds: it is an authoring
 * tool for whoever writes the changelog, not part of reading it.
 */

import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationMediaUpload from './NotificationMediaUpload';

const ChangelogVideoUploader: React.FC = () => {
  const [mediaPath, setMediaPath] = useState('');

  return (
    <Accordion variant="outlined" disableGutters sx={{ mb: 3 }} data-testid="changelog-video-uploader">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">Upload a walkthrough clip</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Upload a clip here, then paste the line below into the entry&apos;s frontmatter. The file is
          stored permanently — add an optional <code>videoExpires: YYYY-MM-DD</code> line to stop showing
          it once it goes out of date.
        </Typography>

        <NotificationMediaUpload
          kind="video"
          label="Walkthrough clip"
          helperText="A short screen recording of the change. Max 100 MB — a 30-second clip is usually 1-5 MB."
          value={mediaPath}
          onChange={setMediaPath}
          testIdPrefix="changelog-video-upload"
        />

        {mediaPath ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              Paste this into the entry&apos;s frontmatter:
            </Typography>
            <Box
              component="code"
              data-testid="changelog-video-frontmatter-line"
              sx={{ display: 'block', wordBreak: 'break-all', fontSize: '0.8rem' }}
            >
              {`video: ${mediaPath}`}
            </Box>
          </Alert>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};

export default ChangelogVideoUploader;
